import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Delete, ClosedCaption, EditNote } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { HANDLES, type CaptionNodeData, type VideoNodeData, useCanvasStore } from '../useCanvasStore';
import { studioService } from '../../../../services/studio.service';
import { useStudioStore } from '../../stores/studio.store';
import CaptionEditorDialog from '../components/CaptionEditorDialog';

function CaptionNode({ id, data }: NodeProps & { data: CaptionNodeData }) {
  const updateNodeData = useCanvasStore((s) => s.updateNodeData);
  const removeNode = useCanvasStore((s) => s.removeNode);
  const edges = useCanvasStore((s) => s.edges);
  const allNodes = useCanvasStore((s) => s.nodes);
  const addAsset = useStudioStore((s) => s.addAsset);
  const projectId = useStudioStore((s) => s.projectId);
  const { enqueueSnackbar } = useSnackbar();
  const [editorOpen, setEditorOpen] = useState(false);

  // Find connected video node
  const videoInEdge = edges.find((e) => e.target === id && e.targetHandle === HANDLES.captionVideoIn);
  const videoNode = videoInEdge ? allNodes.find((n) => n.id === videoInEdge.source) : undefined;
  const sourceVideo = (videoNode?.data as VideoNodeData)?.resultVideo;

  // Sync source video from connected node — only clear captions if there was no previous video
  // (i.e. first connection). Don't clear on URL change (signed URLs change on every load).
  useEffect(() => {
    if (sourceVideo && !data.sourceVideo) {
      updateNodeData(id, { sourceVideo });
    } else if (sourceVideo && sourceVideo !== data.sourceVideo) {
      updateNodeData(id, { sourceVideo });
    }
  }, [sourceVideo, data.sourceVideo, id, updateNodeData]);

  const handleTranscribe = useCallback(async () => {
    if (!data.sourceVideo) {
      enqueueSnackbar('Connect a completed video node first', { variant: 'warning' });
      return;
    }

    updateNodeData(id, { isTranscribing: true, statusMessage: 'Transcribing audio...' });

    try {
      const result = await studioService.transcribeVideo(data.sourceVideo);
      const subtitles = result.subtitles.map((s, i) => ({
        id: `sub-${i}`,
        text: s.text,
        startMs: s.startMs,
        endMs: s.endMs,
        fontSize: 48,
        fontFamily: 'Montserrat',
        color: '#ffffff',
        backgroundColor: '#000000aa',
        strokeColor: '#000000',
        strokeWidth: 2,
        y: 85,
      }));

      updateNodeData(id, {
        isTranscribing: false,
        subtitles,
        hasAudio: result.hasAudio,
        statusMessage: result.hasAudio
          ? `${subtitles.length} caption${subtitles.length === 1 ? '' : 's'} generated`
          : 'No speech detected in video',
      });

      if (result.hasAudio) {
        enqueueSnackbar(`Generated ${subtitles.length} captions — click Edit to customize`, { variant: 'success' });
        setEditorOpen(true);
      } else {
        enqueueSnackbar('No speech detected in this video', { variant: 'info' });
      }
    } catch (err) {
      updateNodeData(id, { isTranscribing: false, statusMessage: 'Transcription failed' });
      enqueueSnackbar((err as Error).message || 'Transcription failed', { variant: 'error' });
    }
  }, [id, data.sourceVideo, updateNodeData, enqueueSnackbar]);

  const handleSaveCaptions = useCallback((subtitles: typeof data.subtitles) => {
    updateNodeData(id, { subtitles });
    enqueueSnackbar('Captions saved', { variant: 'success' });
  }, [id, updateNodeData, enqueueSnackbar]);

  const handleBurn = useCallback(async (subtitles: typeof data.subtitles) => {
    if (!data.sourceVideo || !subtitles?.length) return;

    updateNodeData(id, { isBurning: true, subtitles, statusMessage: 'Burning captions into video...' });

    try {
      const result = await studioService.burnSubtitles(data.sourceVideo, subtitles, projectId || undefined);
      updateNodeData(id, {
        isBurning: false,
        resultVideo: result.videoUrl,
        statusMessage: 'Captions burned successfully',
      });
      addAsset({
        type: 'video',
        data: result.videoUrl,
        mimeType: 'video/mp4',
        prompt: 'Captioned video',
        tool: 'caption',
      });
      setEditorOpen(false);
      enqueueSnackbar('Captioned video ready!', { variant: 'success' });
    } catch (err) {
      updateNodeData(id, { isBurning: false, statusMessage: 'Burn failed' });
      enqueueSnackbar((err as Error).message || 'Failed to burn captions', { variant: 'error' });
    }
  }, [id, data.sourceVideo, projectId, updateNodeData, addAsset, enqueueSnackbar]);

  const isBusy = data.isTranscribing || data.isBurning;
  const hasSubtitles = (data.subtitles?.length ?? 0) > 0;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        width: 260,
        overflow: 'hidden',
      }}
    >
      {/* Video input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id={HANDLES.captionVideoIn}
        style={{ background: '#42a5f5', width: 10, height: 10, top: '50%' }}
        title="Video In"
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          bgcolor: '#7b1fa2',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ClosedCaption sx={{ fontSize: 16 }} />
          <Typography variant="caption" fontWeight={600}>
            Auto Caption
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => removeNode(id)} sx={{ color: 'inherit' }}>
          <Delete sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Connection status */}
        <Chip
          size="small"
          label={sourceVideo ? 'Video connected' : 'No video connected'}
          color={sourceVideo ? 'success' : 'default'}
          variant={sourceVideo ? 'filled' : 'outlined'}
          sx={{ fontSize: '0.7rem', height: 22 }}
        />

        {/* Transcribe button */}
        <Button
          variant="contained"
          size="small"
          onClick={handleTranscribe}
          disabled={!sourceVideo || isBusy}
          startIcon={data.isTranscribing ? <CircularProgress size={14} /> : <ClosedCaption sx={{ fontSize: 14 }} />}
          fullWidth
        >
          {data.isTranscribing ? 'Transcribing...' : hasSubtitles ? 'Re-Transcribe' : 'Generate Captions'}
        </Button>

        {/* Status */}
        {data.statusMessage && (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            {data.statusMessage}
          </Typography>
        )}

        {/* Edit button */}
        {hasSubtitles && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setEditorOpen(true)}
            startIcon={<EditNote sx={{ fontSize: 14 }} />}
            fullWidth
          >
            Edit & Burn ({data.subtitles!.length} captions)
          </Button>
        )}

        {/* Result preview */}
        {data.resultVideo && (
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16/9',
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <video
              src={data.resultVideo}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              muted
              loop
              autoPlay
              playsInline
            />
          </Box>
        )}
      </Box>

      {/* Output handle */}
      {data.resultVideo && (
        <Handle
          type="source"
          position={Position.Right}
          id={HANDLES.videoOut}
          style={{ background: '#42a5f5', width: 10, height: 10 }}
        />
      )}

      {/* Editor dialog */}
      {data.sourceVideo && (
        <CaptionEditorDialog
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          videoUrl={data.sourceVideo}
          subtitles={data.subtitles || []}
          onSave={handleSaveCaptions}
          onBurn={handleBurn}
          isBurning={data.isBurning}
        />
      )}
    </Box>
  );
}

export default memo(CaptionNode);
